// stargazer, relativity, discovery
import {DATA_TRANSCLUDE, ElementSetup, Engine} from '@acng/frontend-stargazer';
import {Watch, localValue} from '@acng/frontend-relativity';
import {createPerceptiveHTML, createStream, popup} from '@acng/frontend-discovery';
// bounty
import {createWeakMap, get, set} from '@acng/frontend-bounty/collection.js';
import {size} from '@acng/frontend-bounty/std/array.js';
import {iterate} from '@acng/frontend-bounty/object.js';
import {append, remove} from '@acng/frontend-bounty/dom/move.js';
import {has, set as setAttribute} from '@acng/frontend-bounty/dom/attribute.js';
import {HTMLElement, con, def, dis} from '@acng/frontend-bounty/dom/custom.js';
import {DIV, TEMPLATE, all, create} from '@acng/frontend-bounty/dom/element.js';
import {debug} from '@acng/frontend-bounty/dom/debug.js';

import {ctxList} from '../context/list.js';
import {USE_HISTORY} from '../attribute/use-history.js';

/**
 * @group DOM Element
 */
export const NOVA_LIST = 'nova-list';

ElementSetup(NOVA_LIST, (name) => {
  const transclude = Engine.Transclude(name, TEMPLATE);
  const watchList = Watch(name, ctxList);
  /**
   * @type {WeakMap<import('@acng/frontend-discovery').Loader, Engine>}
   */
  const engines = createWeakMap();

  class ListElement extends HTMLElement {
    /**
     * @type {HTMLElement | undefined}
     */
    #empty;
    #list = localValue(this, ctxList);
    #engine = transclude(this);

    #stream = createStream(this.#list.initial, async (offset) => {
      try {
        const more = await this.#list.more(offset);

        if (!offset && !size(more)) {
          if (!this.#empty) {
            this.#empty = create(DIV);
            append(this, this.#engine.spawn('empty').toElement(this.#empty));
          }
        } else if (this.#empty) {
          remove(this.#empty);
          this.#empty = void 0;
        }

        return more;
      } catch (reason) {
        popup(this).error(`${reason}`);

        return [];
      }
    });

    #warpdrive = createPerceptiveHTML(
      this,
      this.#stream,
      async (entry, loader) => {
        const link = await this.#list.ItemLink(entry);

        if (loader.target) {
          const {target} = loader;
          const linker = this.#engine.spawn('repeat');
          const fragment = linker.link({}, target, link);

          append(target, fragment);
          linker.connect();
          set(engines, loader, linker);
        }
      },
      (loader) => get(engines, loader)?.disconnect(),
    );

    constructor() {
      super();
      DEBUG: if (debug(this)) this.#warpdrive.enableDebug(name);
    }

    [con]() {
      if (has(this, USE_HISTORY)) {
        this.#warpdrive.enableHistory(name);
      }

      watchList(this, (_element, list) => {
        if (this.#empty) {
          remove(this.#empty);
          this.#empty = void 0;
        }

        this.#list = list;
        this.#stream.streamReset(list.initial);
        this.#warpdrive.connect();
      });
    }

    [dis]() {
      this.#warpdrive.disconnect();
    }
  }

  def(name, ListElement);

  return (element) => iterate(all(element, TEMPLATE), (template) => setAttribute(template, DATA_TRANSCLUDE));
});
